import { Subscription } from "rxjs";
import { DefaultTableModel } from "./TableModel";
import { ColumnDef } from "./types";
import {
  TableModel as QubesTableModel,
  ColumnDef as QubesColumnDef,
  RowChange as QubesRowChange,
  RowIdT,
} from "../qubes-table/table-model";

/**
 * Adapts a qubes SqlFirewall `TableModel` (built by `createSqlFirewallTableModel`)
 * onto the sql-query `Table` component's `ITableModel`, so SqlFirewallView renders
 * through the same widget the SQL editor uses — with global search, the column
 * chooser, resizing and the fat scrollbars — while preserving field-aware cell
 * rendering (each cell still renders via the qubes `FieldDef.getDisplayComponent`).
 *
 * Modeled on `RpcTableModel`: extend `DefaultTableModel`, seed `data` from the
 * qubes model's `currentRows`, and bridge its `realtime` rxjs Subject into `data`.
 * The qubes model is reused verbatim, so CDC refresh, the drill-down gate,
 * refreshInterval and key remapping keep working unchanged — this adapter only
 * feeds the view layer.
 */
export class SqlFirewallTableModel extends DefaultTableModel<Record<string, unknown>> {
  private readonly qubesModel: QubesTableModel<Record<string, unknown>>;
  private readonly rowIdFn: (row: Record<string, unknown>) => RowIdT;
  private subscription?: Subscription;
  private refreshSubscription?: Subscription;
  private updateCallback?: () => void;
  /**
   * What the table is doing, for the loading / no-data / error states
   * (FEATURE-20260825-table-loading-vs-empty-state). Fed by the qubes model's
   * refreshState: a fetch in flight, the last fetch's error, and whether any
   * fetch has completed yet (before that, "no rows" means nothing).
   */
  loading = false;
  loadedOnce = false;
  error?: string;
  /** the query the failed fetch sent, for the error panel's "Show query" */
  errorQuery?: string;
  /** the model refetches on this period, so a failure is retried */
  retryEveryMs?: number;
  /** when the last successful fetch completed — the age of rows shown after a failure */
  lastRefreshedAt?: number;
  /** the view runs without its optional columns (see RefreshState.degraded) */
  degraded?: { columns: string[]; labels: string[]; reason: string };
  /** Column ids that carry an explicit FieldDef.width — never auto-sized. */
  private readonly widthOverrides = new Set<string>();

  constructor(qubesModel: QubesTableModel<Record<string, unknown>>, onUpdate?: () => void) {
    const rows = [...qubesModel.currentRows.values()];
    super(SqlFirewallTableModel.mapColumns(qubesModel.columns, rows), rows);
    this.qubesModel = qubesModel;
    this.rowIdFn = (row) => qubesModel.rowId(row);
    this.updateCallback = onUpdate;
    // getRowId lets Table key rows stably (change tracking, selection).
    this.getRowId = (row) => qubesModel.rowId(row);
    for (const qcol of qubesModel.columns) {
      if (typeof qcol.fieldDef?.width === "number") {
        this.widthOverrides.add(qcol.muiColDef.field);
      }
    }

    if (qubesModel.realtime) {
      this.subscription = qubesModel.realtime.subscribe({
        next: (change) => this.handleRowChange(change),
      });
    }
    if (qubesModel.refreshState) {
      this.refreshSubscription = qubesModel.refreshState.subscribe({
        next: (state) => {
          this.loading = state.isRefreshing;
          if (!state.isRefreshing) {
            this.loadedOnce = true;
            this.error = state.error;
            this.errorQuery = state.errorQuery;
            this.retryEveryMs = state.retryEveryMs;
            this.lastRefreshedAt = state.lastRefreshedAt;
            this.degraded = state.degraded;
          }
          this.updateCallback?.();
        },
      });
    }
  }

  /**
   * Map each qubes `ColumnDef` to a sql-query `ColumnDef`. The load-bearing part
   * is `cellRenderer`, which wraps the qubes `renderCell` (i.e.
   * `FieldDef.getDisplayComponent`) so every field type renders exactly as it did
   * under MUI. `accessor` is still needed for search/sort even when a renderer is
   * present. We deliberately do NOT infer a DataType from the field name (that was
   * the field-awareness regression in BiqlTableModel).
   */
  private static mapColumns(
    qcols: QubesColumnDef<Record<string, unknown>>[],
    rows: Record<string, unknown>[],
  ): ColumnDef<Record<string, unknown>>[] {
    return qcols.map((qcol) => {
      const field = qcol.muiColDef.field;
      const valueGetter = qcol.valueGetter;
      const renderCell = qcol.renderCell;
      const accessor = valueGetter ? (row: Record<string, unknown>) => valueGetter(row) : (row: Record<string, unknown>) => row?.[field];
      const header = qcol.muiColDef.headerName ?? field;
      const col: ColumnDef<Record<string, unknown>> = {
        id: field,
        header,
        // Width priority: explicit FieldDef.width override → data-driven auto-size.
        // We lean into the full-dataset-in-memory model, so measuring every loaded
        // value is cheap and gives sane per-column widths (vs a flat 150px).
        width:
          typeof qcol.fieldDef?.width === "number"
            ? qcol.fieldDef.width
            : SqlFirewallTableModel.autoWidth(header, accessor, rows),
        accessor,
        visible: !(qcol.hidden ?? false),
        sortable: true,
        resizable: true,
      };
      if (renderCell) {
        col.cellRenderer = (_value, row) => renderCell(row);
      }
      const sortValue = qcol.fieldDef?.getSortValue;
      if (sortValue) {
        const fd = qcol.fieldDef;
        col.sortFn = (a, b) => {
          // Sort values are the field's dynamic cell values (number/string/Date);
          // compare as primitives — JS < / > keep the right ordering at runtime.
          const av = sortValue(a, fd) as number | string;
          const bv = sortValue(b, fd) as number | string;
          return av < bv ? -1 : av > bv ? 1 : 0;
        };
      }
      return col;
    });
  }

  /**
   * Measure a sane column width from the header and the loaded cell values using
   * pixel-accurate canvas text measurement (not a per-char estimate, which was
   * consistently too small for the grid font). The header reserves room for the
   * sort caret; body cells add cell padding. Clamped to [MIN, MAX].
   *
   * NB: this must run AFTER rows arrive. For child (drill-down) views the qubes
   * model's currentRows is empty at construction — rows land later via the
   * realtime Subject — so we also recompute on data change (see recomputeWidths).
   */
  private static readonly PADDING = 32; // ~16px each side of cell content
  private static readonly SORT_CARET = 26; // header label + sort indicator + gap
  private static readonly MIN = 96;
  private static readonly MAX = 560;
  private static measureCtx: CanvasRenderingContext2D | null = null;

  private static textWidth(text: string): number {
    if (!SqlFirewallTableModel.measureCtx) {
      const canvas =
        typeof document !== "undefined" ? document.createElement("canvas") : null;
      SqlFirewallTableModel.measureCtx = canvas ? canvas.getContext("2d") : null;
      if (SqlFirewallTableModel.measureCtx) {
        // Match the grid's cell font. Keep in sync with Table.scss.
        SqlFirewallTableModel.measureCtx.font =
          "14px Roboto, Helvetica, Arial, sans-serif";
      }
    }
    const ctx = SqlFirewallTableModel.measureCtx;
    if (!ctx) return text.length * 8.5; // headless fallback (tests)
    return ctx.measureText(text).width;
  }

  private static autoWidth(
    header: string,
    accessor: (row: Record<string, unknown>) => unknown,
    rows: Record<string, unknown>[],
  ): number {
    const headerPx =
      SqlFirewallTableModel.textWidth(header ?? "") +
      SqlFirewallTableModel.PADDING +
      SqlFirewallTableModel.SORT_CARET;
    let bodyPx = 0;
    const sample = rows.length > 500 ? rows.slice(0, 500) : rows;
    for (const row of sample) {
      const v = accessor(row);
      if (v == null) continue;
      const w = SqlFirewallTableModel.textWidth(String(v));
      if (w > bodyPx) bodyPx = w;
    }
    bodyPx += SqlFirewallTableModel.PADDING;
    return Math.round(
      Math.max(
        SqlFirewallTableModel.MIN,
        Math.min(SqlFirewallTableModel.MAX, Math.max(headerPx, bodyPx)),
      ),
    );
  }

  /**
   * Recompute every auto-sized column's width from the current `this.data`.
   * Called after the realtime Subject delivers the initial/refreshed row set,
   * because for drill-down views there are no rows at construction time. Columns
   * with an explicit FieldDef.width override are left untouched.
   */
  private recomputeWidths(): void {
    // 1. Content-size every auto column (pixel-accurate from loaded data).
    for (const col of this.columns) {
      if (this.widthOverrides.has(col.id)) continue; // explicit width wins
      col.width = SqlFirewallTableModel.autoWidth(
        col.header,
        col.accessor,
        this.data,
      );
    }
    // 2. If the content is narrower than the available panel, let the text
    //    columns absorb the slack so the rows fill the panel (see distributeSlack).
    this.distributeSlack();
  }

  /** Available panel width in px (from the view's ResizeObserver), 0 = unknown. */
  private availableWidth = 0;

  /**
   * Set the panel's available width and re-pad the columns. Called by the view
   * when its ResizeObserver reports a size (initial + on resize).
   */
  setAvailableWidth(px: number): void {
    if (px === this.availableWidth) return;
    this.availableWidth = px;
    this.recomputeWidths();
    this.updateCallback?.();
  }

  /**
   * Fill the panel the way the monitors DataGrid does with `flex`: text columns
   * absorb ALL the slack, in proportion to their content width. Columns whose
   * loaded values are all numbers/booleans (counts, flags) keep their measured
   * width, as do columns with an explicit FieldDef.width — a 500px-wide column
   * of two-digit counts is worse than an unfilled panel. When no text column is
   * visible nothing grows and the gap stays.
   */
  private distributeSlack(): void {
    const avail = this.availableWidth;
    if (!avail || avail <= 0) return;
    const cols = this.columns.filter((c) => c.visible !== false);
    if (!cols.length) return;
    const total = cols.reduce((s, c) => s + (c.width || 0), 0);
    const slack = avail - total;
    if (slack === 0) return;
    const flex = cols.filter(
      (c) => !this.widthOverrides.has(c.id) && this.isTextColumn(c),
    );
    const flexTotal = flex.reduce((s, c) => s + (c.width || 0), 0);
    if (flexTotal <= 0) return;
    if (slack > 0) {
      flex.forEach((c) => {
        c.width = Math.round((c.width || 0) + slack * ((c.width || 0) / flexTotal));
      });
      return;
    }
    // Wider than the panel: take the overflow out of the text columns in
    // proportion, but never below MIN — past that the panel scrolls sideways,
    // which is the honest answer for a genuinely wide table.
    const room = flex.reduce((s, c) => s + Math.max(0, (c.width || 0) - SqlFirewallTableModel.MIN), 0);
    if (room <= 0) return;
    const shrink = Math.min(-slack, room);
    flex.forEach((c) => {
      const give = Math.max(0, (c.width || 0) - SqlFirewallTableModel.MIN);
      c.width = Math.round((c.width || 0) - shrink * (give / room));
    });
  }

  /**
   * A column is text unless every loaded value is a number, a boolean, or a
   * numeric string. A column with no values yet (a nullable description, an
   * empty drill-down) counts as text so it still flexes.
   */
  private isTextColumn(col: ColumnDef<Record<string, unknown>>): boolean {
    const sample = this.data.length > 500 ? this.data.slice(0, 500) : this.data;
    let sawValue = false;
    for (const row of sample) {
      const v = col.accessor(row);
      if (v == null) continue;
      sawValue = true;
      if (typeof v === "number" || typeof v === "boolean") continue;
      if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v))) continue;
      return true;
    }
    return !sawValue;
  }

  private handleRowChange(change: QubesRowChange<Record<string, unknown>>): void {
    if (change.type === "upsert") {
      const row = change.row;
      const rowId = this.rowIdFn(row);
      const existingIndex = this.data.findIndex((r) => this.rowIdFn(r) === rowId);
      if (existingIndex >= 0) {
        this.data[existingIndex] = row;
      } else {
        this.data.push(row);
      }
    } else if (change.type === "delete") {
      const index = this.data.findIndex((r) => this.rowIdFn(r) === change.rowId);
      if (index >= 0) {
        this.data.splice(index, 1);
      }
    }
    // A fetch/refresh delivers one change per row (32 tables → 32 upserts);
    // debounce the width recompute + re-render to once per settled batch.
    this.scheduleRecompute();
  }

  private recomputeScheduled = false;
  /**
   * Coalesce a burst of row changes into a single width recompute + re-render on
   * the next microtask, so a 32-row fetch resizes columns once, not 32 times.
   */
  private scheduleRecompute(): void {
    if (this.recomputeScheduled) return;
    this.recomputeScheduled = true;
    Promise.resolve().then(() => {
      this.recomputeScheduled = false;
      this.recomputeWidths();
      this.updateCallback?.();
    });
  }

  /** Delegate a programmatic refresh (drill-down gate) to the qubes model. */
  refreshQuery(filters?: Parameters<NonNullable<QubesTableModel<Record<string, unknown>>["refresh"]>>[0]): Promise<void> {
    return this.qubesModel.refresh?.(filters) ?? Promise.resolve();
  }

  dispose(): void {
    this.subscription?.unsubscribe();
    this.refreshSubscription?.unsubscribe();
  }
}
